import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateSection, setSection } from "../../../assignmentSlice";
import { getContentHeight } from "../../../../../utils/textUtils";
import { buildBookActivityList, isSameAnswer, PART_LABEL, seededShuffle } from "./bookActivities";
import { crosswordWordList, gradeActivity, reportActivity } from "./bookGrading";
import "./BookWorkbookPage.css";

// 원서 워크북(지인에듀 mode=book) 학생 화면. 세트 하나가 섹션 하나로 오고(question.data = 장 한 벌),
// 활동을 한 화면에 하나씩 넘기며 푼다. 디자인은 우리 내용이해 화면(주황, 검은 Q칩)을 따른다.
//
// 저장: Section.answer 의 첫 줄은 화면 상태({type: "book_state"})이고 나머지는 채점 기록이다.
// 채점 기록은 문항마다 {activity, index, isCorrect} 이고, 서술형처럼 채점하지 않는 문항은
// isCorrect 가 null 이라 점수(calculate_grade)와 복습(wrong_answer)에서 빠진다.

const STATE_TYPE = "book_state";

const isNarrowScreen = () =>
  typeof window !== "undefined" && Math.min(window.innerWidth, window.screen.width) <= 768;

const readSavedState = (answerList) => {
  const saved = (answerList || []).find((one) => one && one.type === STATE_TYPE);
  return {
    responses: (saved && saved.responses) || {},
    checkedKeys: (saved && saved.checkedKeys) || [],
    results: (saved && saved.results) || {},
  };
};

const renderBold = (text) =>
  String(text || "")
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**") ? <b key={index}>{part.slice(2, -2)}</b> : part
    );

// ───────── 보기 고르기 ─────────
const ChoiceActivity = ({ activity, response, onChange, checked }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => {
        const picked = value[itemIndex];
        return (
          <div className="book-workbook__item" key={itemIndex}>
            <div className="book-workbook__item-head">
              <span className="book-workbook__item-number">{itemIndex + 1}</span>
              <div className="book-workbook__item-prompt">{renderBold(item.prompt)}</div>
            </div>
            {item.quote && <div className="book-workbook__quote">{renderBold(item.quote)}</div>}
            <div className={`book-workbook__options${activity.isInline ? " book-workbook__options--inline" : ""}`}>
              {item.options.map((option, optionIndex) => {
                const isPicked = picked === optionIndex;
                const isAnswer = checked && optionIndex === item.answerIndex;
                const isWrongPick = checked && isPicked && optionIndex !== item.answerIndex;
                return (
                  <button
                    type="button"
                    key={optionIndex}
                    disabled={checked}
                    className={`book-workbook__option${isPicked ? " is-picked" : ""}${isAnswer ? " is-answer" : ""}${
                      isWrongPick ? " is-wrong" : ""
                    }`}
                    onClick={() => onChange({ ...value, [itemIndex]: optionIndex })}
                  >
                    {!activity.isInline && <span className="book-workbook__option-mark">{optionIndex + 1}</span>}
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
            {checked && (item.explanation || item.evidence) && (
              <div className="book-workbook__explain">
                {item.explanation && <div>{item.explanation}</div>}
                {item.evidence && <div className="book-workbook__evidence">{item.evidence}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ───────── 한 줄 입력 ─────────
const InputActivity = ({ activity, response, onChange, checked, resultList }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.bankList && (
        <div className="book-workbook__bank">
          {activity.bankList.map((word, index) => (
            <span key={index}>{word}</span>
          ))}
        </div>
      )}
      {activity.items.map((item, itemIndex) => {
        const result = resultList ? resultList[itemIndex] : null;
        return (
          <div className="book-workbook__item" key={itemIndex}>
            <div className="book-workbook__item-head">
              <span className="book-workbook__item-number">{itemIndex + 1}</span>
              <div className={`book-workbook__item-prompt${item.isLetterPuzzle ? " book-workbook__letters" : ""}`}>
                {renderBold(item.prompt)}
              </div>
              {checked && result !== null && result !== undefined && (
                <span className={`book-workbook__mark ${result ? "is-right" : "is-wrong"}`}>{result ? "O" : "X"}</span>
              )}
            </div>
            {item.subPrompt && <div className="book-workbook__sub-prompt">{item.subPrompt}</div>}
            {item.quote && <div className="book-workbook__quote">{renderBold(item.quote)}</div>}
            <input
              className="book-workbook__line-input"
              value={value[itemIndex] || ""}
              disabled={checked}
              placeholder={item.hint ? item.hint : item.placeholder || "답을 입력해주세요."}
              autoCapitalize="off"
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => onChange({ ...value, [itemIndex]: event.target.value })}
            />
            {checked && item.answer && result !== true && (
              <div className="book-workbook__explain">
                <div>
                  <b>{item.gradeable ? "정답" : "모범 답"}</b> {item.answer}
                </div>
                {item.explanation && <div>{item.explanation}</div>}
                {item.evidence && <div className="book-workbook__evidence">{item.evidence}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ───────── 여러 줄 쓰기 ─────────
const WrittenActivity = ({ activity, response, onChange, checked }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => (
        <div className="book-workbook__item" key={itemIndex}>
          <div className="book-workbook__item-head">
            {activity.items.length > 1 && <span className="book-workbook__item-number">{itemIndex + 1}</span>}
            <div className="book-workbook__item-prompt">{renderBold(item.prompt)}</div>
          </div>
          {item.quote && <div className="book-workbook__quote">{renderBold(item.quote)}</div>}
          {item.helpList && item.helpList.length > 0 && (
            <ul className="book-workbook__help">
              {item.helpList.map((help, index) => (
                <li key={index}>{help}</li>
              ))}
            </ul>
          )}
          <textarea
            className="book-workbook__textarea"
            rows={activity.isEssay ? 10 : 4}
            value={value[itemIndex] || ""}
            disabled={checked}
            placeholder="여기에 쓰세요."
            onChange={(event) => onChange({ ...value, [itemIndex]: event.target.value })}
          />
          {item.rubricList && item.rubricList.length > 0 && (
            <div className="book-workbook__rubric">
              {item.rubricList.map((rubric, index) => (
                <div key={index}>
                  <b>{rubric.criterion}</b> {rubric.description}
                </div>
              ))}
            </div>
          )}
          {checked && item.answer && (
            <div className="book-workbook__explain">
              <div>
                <b>모범 답</b> {item.answer}
              </div>
              {item.explanation && <div>{item.explanation}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ───────── 선 긋기 ─────────
const PAIR_COLOR_LIST = ["#E8920A", "#2F6FDE", "#1DB05B", "#8A4FD8", "#C2416B", "#0E7490", "#5B7B0F", "#B45309"];

const MatchBoard = ({ item, seedText, value, onChange, checked }) => {
  const boardRef = useRef(null);
  const [lineList, setLineList] = useState([]);
  const [activeLeft, setActiveLeft] = useState(null);
  const rightOrder = useMemo(() => seededShuffle(item.pairs.map((_, index) => index), seedText), [item, seedText]);
  const pairs = value || {};
  const leftOfRight = {};
  Object.keys(pairs).forEach((left) => {
    leftOfRight[pairs[left]] = Number(left);
  });

  useLayoutEffect(() => {
    const draw = () => {
      const board = boardRef.current;
      if (!board) return;
      const boardRect = board.getBoundingClientRect();
      const nextLineList = Object.keys(pairs).map((left) => {
        const leftDot = board.querySelector(`[data-left-dot="${left}"]`);
        const rightDot = board.querySelector(`[data-right-dot="${pairs[left]}"]`);
        if (!leftDot || !rightDot) return null;
        const leftRect = leftDot.getBoundingClientRect();
        const rightRect = rightDot.getBoundingClientRect();
        return {
          left: Number(left),
          x1: leftRect.left + leftRect.width / 2 - boardRect.left,
          y1: leftRect.top + leftRect.height / 2 - boardRect.top,
          x2: rightRect.left + rightRect.width / 2 - boardRect.left,
          y2: rightRect.top + rightRect.height / 2 - boardRect.top,
        };
      });
      setLineList(nextLineList.filter(Boolean));
    };
    draw();
    window.addEventListener("resize", draw);
    return () => window.removeEventListener("resize", draw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pairs)]);

  const connect = (leftIndex, rightIndex) => {
    const next = { ...pairs };
    Object.keys(next).forEach((left) => {
      if (next[left] === rightIndex) delete next[left];
    });
    next[leftIndex] = rightIndex;
    onChange(next);
    setActiveLeft(null);
  };

  const pickLeft = (leftIndex) => {
    if (checked) return;
    if (pairs[leftIndex] !== undefined) {
      const next = { ...pairs };
      delete next[leftIndex];
      onChange(next);
      return;
    }
    setActiveLeft(activeLeft === leftIndex ? null : leftIndex);
  };

  const pickRight = (rightIndex) => {
    if (checked) return;
    if (activeLeft !== null) {
      connect(activeLeft, rightIndex);
    } else if (leftOfRight[rightIndex] !== undefined) {
      const next = { ...pairs };
      delete next[leftOfRight[rightIndex]];
      onChange(next);
    }
  };

  return (
    <div className="book-workbook__match" ref={boardRef}>
      <svg className="book-workbook__match-lines" aria-hidden="true">
        {lineList.map((line) => {
          const isWrong = checked && pairs[line.left] !== line.left;
          return (
            <line
              key={line.left}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={PAIR_COLOR_LIST[line.left % PAIR_COLOR_LIST.length]}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={isWrong ? "10 8" : undefined}
            />
          );
        })}
      </svg>
      <div className="book-workbook__match-column">
        {item.pairs.map((pair, leftIndex) => {
          const isConnected = pairs[leftIndex] !== undefined;
          const color = PAIR_COLOR_LIST[leftIndex % PAIR_COLOR_LIST.length];
          return (
            <button
              type="button"
              key={leftIndex}
              className={`book-workbook__match-item${activeLeft === leftIndex ? " is-active" : ""}`}
              style={isConnected ? { borderColor: color } : undefined}
              onClick={() => pickLeft(leftIndex)}
            >
              {pair.leftImage ? <img src={pair.leftImage} alt="" /> : <span>{pair.left}</span>}
              <i
                data-left-dot={leftIndex}
                className="book-workbook__dot book-workbook__dot--left"
                style={isConnected ? { borderColor: color, background: color } : undefined}
              />
            </button>
          );
        })}
      </div>
      <div />
      <div className="book-workbook__match-column">
        {rightOrder.map((rightIndex) => {
          const owner = leftOfRight[rightIndex];
          const isConnected = owner !== undefined;
          const color = isConnected ? PAIR_COLOR_LIST[owner % PAIR_COLOR_LIST.length] : undefined;
          const isRight = checked && isConnected && owner === rightIndex;
          return (
            <button
              type="button"
              key={rightIndex}
              className="book-workbook__match-item book-workbook__match-item--right"
              style={isConnected ? { borderColor: color } : undefined}
              onClick={() => pickRight(rightIndex)}
            >
              <i
                data-right-dot={rightIndex}
                className="book-workbook__dot book-workbook__dot--right"
                style={isConnected ? { borderColor: color, background: color } : undefined}
              />
              <span>{item.pairs[rightIndex].right}</span>
              {checked && isConnected && (
                <b className={`book-workbook__mark ${isRight ? "is-right" : "is-wrong"}`}>{isRight ? "O" : "X"}</b>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const MatchActivity = ({ activity, response, onChange, checked, seedText }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => (
        <div className="book-workbook__item" key={itemIndex}>
          {item.prompt && <div className="book-workbook__item-prompt">{item.prompt}</div>}
          <MatchBoard
            item={item}
            seedText={`${seedText}:${itemIndex}`}
            value={value[itemIndex]}
            checked={checked}
            onChange={(next) => onChange({ ...value, [itemIndex]: next })}
          />
          {checked && (
            <div className="book-workbook__explain">
              {item.pairs.map((pair, index) => (
                <div key={index}>
                  <b>{pair.left}</b> ↔ {pair.right}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ───────── 순서 맞추기 ─────────
const OrderActivity = ({ activity, response, onChange, checked, seedText, resultList }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => {
        const shuffledList = seededShuffle(item.correctList.map((_, index) => index), `${seedText}:${itemIndex}`);
        const pickedList = value[itemIndex] || [];
        const result = resultList ? resultList[itemIndex] : null;
        const toggle = (originalIndex) => {
          if (checked) return;
          const next = pickedList.includes(originalIndex)
            ? pickedList.filter((one) => one !== originalIndex)
            : [...pickedList, originalIndex];
          onChange({ ...value, [itemIndex]: next });
        };
        return (
          <div className="book-workbook__item" key={itemIndex}>
            <div className="book-workbook__item-head">
              {activity.items.length > 1 && <span className="book-workbook__item-number">{itemIndex + 1}</span>}
              <div className="book-workbook__item-prompt">{item.prompt || "순서대로 누르세요."}</div>
              {checked && <span className={`book-workbook__mark ${result ? "is-right" : "is-wrong"}`}>{result ? "O" : "X"}</span>}
            </div>
            {activity.isSentence && pickedList.length > 0 && (
              <div className="book-workbook__built">{pickedList.map((index) => item.correctList[index]).join(" ")}</div>
            )}
            <div className={`book-workbook__order${activity.isSentence ? " book-workbook__order--chips" : ""}`}>
              {shuffledList.map((originalIndex) => {
                const position = pickedList.indexOf(originalIndex);
                return (
                  <button
                    type="button"
                    key={originalIndex}
                    disabled={checked}
                    className={`book-workbook__order-item${position >= 0 ? " is-picked" : ""}`}
                    onClick={() => toggle(originalIndex)}
                  >
                    {!activity.isSentence && <span className="book-workbook__order-number">{position >= 0 ? position + 1 : ""}</span>}
                    <span>{item.correctList[originalIndex]}</span>
                  </button>
                );
              })}
            </div>
            {checked && !result && (
              <div className="book-workbook__explain">
                <b>정답</b>
                {activity.isSentence ? (
                  <div>{item.correctList.join(" ")}</div>
                ) : (
                  <ol>
                    {item.correctList.map((one, index) => (
                      <li key={index}>{one}</li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ───────── 여러 개 고르기 ─────────
const MultiActivity = ({ activity, response, onChange, checked }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => {
        const pickedList = value[itemIndex] || [];
        return (
          <div className="book-workbook__item" key={itemIndex}>
            {item.prompt && <div className="book-workbook__quote">{item.prompt}</div>}
            <div className="book-workbook__sub-prompt">
              {item.answerIndexList.length}개를 고르세요. ({pickedList.length}/{item.answerIndexList.length})
            </div>
            <div className="book-workbook__options">
              {item.options.map((option, optionIndex) => {
                const isPicked = pickedList.includes(optionIndex);
                const isAnswer = checked && item.answerIndexList.includes(optionIndex);
                return (
                  <button
                    type="button"
                    key={optionIndex}
                    disabled={checked}
                    className={`book-workbook__option${isPicked ? " is-picked" : ""}${isAnswer ? " is-answer" : ""}${
                      checked && isPicked && !isAnswer ? " is-wrong" : ""
                    }`}
                    onClick={() => {
                      const next = isPicked
                        ? pickedList.filter((one) => one !== optionIndex)
                        : pickedList.length < item.answerIndexList.length
                        ? [...pickedList, optionIndex]
                        : pickedList;
                      onChange({ ...value, [itemIndex]: next });
                    }}
                  >
                    <span className="book-workbook__option-mark">{optionIndex + 1}</span>
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
            {checked && item.explanation && <div className="book-workbook__explain">{item.explanation}</div>}
          </div>
        );
      })}
    </div>
  );
};

// ───────── 범주 나누기 ─────────
const ClassifyActivity = ({ activity, response, onChange, checked }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => {
        const pickedByRow = value[itemIndex] || {};
        return (
          <div className="book-workbook__item" key={itemIndex}>
            {item.prompt && <div className="book-workbook__item-prompt">{item.prompt}</div>}
            {item.rowList.map((row, rowIndex) => {
              const picked = pickedByRow[rowIndex];
              const isRight = checked && picked === row.answerIndex;
              return (
                <div className="book-workbook__classify-row" key={rowIndex}>
                  <div className="book-workbook__classify-text">
                    {row.text}
                    {checked && <span className={`book-workbook__mark ${isRight ? "is-right" : "is-wrong"}`}>{isRight ? "O" : "X"}</span>}
                  </div>
                  <div className="book-workbook__options book-workbook__options--inline">
                    {item.categoryList.map((category, categoryIndex) => (
                      <button
                        type="button"
                        key={categoryIndex}
                        disabled={checked}
                        className={`book-workbook__option${picked === categoryIndex ? " is-picked" : ""}${
                          checked && categoryIndex === row.answerIndex ? " is-answer" : ""
                        }${checked && picked === categoryIndex && !isRight ? " is-wrong" : ""}`}
                        onClick={() => onChange({ ...value, [itemIndex]: { ...pickedByRow, [rowIndex]: categoryIndex } })}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// ───────── 빈칸 채우기 + 단어 상자 ─────────
const GapActivity = ({ activity, response, onChange, checked }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => {
        const filled = value[itemIndex] || {};
        const pieceList = item.text.split(/\((\d)\)_+/g);
        return (
          <div className="book-workbook__item" key={itemIndex}>
            <div className="book-workbook__bank">
              {item.bankList.map((word, index) => (
                <span key={index}>{word}</span>
              ))}
            </div>
            <div className="book-workbook__gap-text">
              {pieceList.map((piece, index) => {
                if (index % 2 === 0) return <span key={index}>{piece}</span>;
                const blankIndex = Number(piece) - 1;
                const isRight = checked && isSameAnswer(filled[blankIndex], item.answerList[blankIndex]);
                return (
                  <span key={index} className="book-workbook__gap-slot">
                    <small>({piece})</small>
                    <input
                      value={filled[blankIndex] || ""}
                      disabled={checked}
                      autoCapitalize="off"
                      autoComplete="off"
                      spellCheck={false}
                      className={checked ? (isRight ? "is-right" : "is-wrong") : ""}
                      onChange={(event) =>
                        onChange({ ...value, [itemIndex]: { ...filled, [blankIndex]: event.target.value } })
                      }
                    />
                    {checked && !isRight && <em>{item.answerList[blankIndex]}</em>}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ───────── 표 채우기 ─────────
const TableActivity = ({ activity, response, onChange, checked }) => {
  const value = response || {};
  return (
    <div className="book-workbook__item-list">
      {activity.items.map((item, itemIndex) => {
        const filled = value[itemIndex] || {};
        return (
          <div className="book-workbook__item" key={itemIndex}>
            {item.prompt && <div className="book-workbook__item-prompt">{item.prompt}</div>}
            <div className="book-workbook__table-wrap">
              <table className="book-workbook__table">
                <thead>
                  <tr>
                    <th />
                    {item.headerList.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {item.rowList.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <th>{row.label}</th>
                      {item.headerList.map((_, columnIndex) => {
                        const cellKey = `${rowIndex}:${columnIndex}`;
                        return (
                          <td key={columnIndex}>
                            <textarea
                              rows={2}
                              value={filled[cellKey] || ""}
                              disabled={checked}
                              onChange={(event) =>
                                onChange({ ...value, [itemIndex]: { ...filled, [cellKey]: event.target.value } })
                              }
                            />
                            {checked && row.answerList[columnIndex] && (
                              <div className="book-workbook__table-answer">{row.answerList[columnIndex]}</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ───────── 크로스워드 ─────────
const CrosswordActivity = ({ activity, response, onChange, checked, resultList }) => {
  const crossword = activity.crossword;
  const wordList = useMemo(() => crosswordWordList(crossword), [crossword]);
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [draft, setDraft] = useState("");
  const value = response || {};
  const letters = value.letters || {};
  const activeWord = wordList[activeWordIndex];
  const cellByKey = {};
  (crossword.cells || []).forEach((cell) => {
    cellByKey[`${cell.row}:${cell.col}`] = cell;
  });
  const activeKeySet = new Set((activeWord ? activeWord.cellList : []).map((cell) => `${cell.row}:${cell.col}`));
  const cellSize = Math.max(18, Math.min(34, Math.floor((Math.min(window.innerWidth, 720) - 48) / (crossword.cols || 12))));

  useEffect(() => {
    if (!activeWord) return;
    setDraft(activeWord.cellList.map((cell) => letters[`${cell.row}:${cell.col}`] || "").join("").trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWordIndex]);

  const writeWord = (text) => {
    const cleaned = text.replace(/[^a-zA-Z]/g, "").slice(0, activeWord.cellList.length);
    setDraft(cleaned);
    const nextLetters = { ...letters };
    activeWord.cellList.forEach((cell, index) => {
      nextLetters[`${cell.row}:${cell.col}`] = cleaned[index] ? cleaned[index].toUpperCase() : "";
    });
    onChange({ ...value, letters: nextLetters });
  };

  const selectCell = (cellKey) => {
    const matchList = wordList
      .map((word, index) => ({ word, index }))
      .filter(({ word }) => word.cellList.some((cell) => `${cell.row}:${cell.col}` === cellKey));
    if (matchList.length === 0) return;
    const currentPosition = matchList.findIndex(({ index }) => index === activeWordIndex);
    setActiveWordIndex(matchList[(currentPosition + 1) % matchList.length].index);
  };

  const wrongKeySet = new Set();
  if (checked && resultList) {
    wordList.forEach((word, index) => {
      if (resultList[index] === false) {
        word.cellList.forEach((cell) => {
          const key = `${cell.row}:${cell.col}`;
          if ((letters[key] || "").toLowerCase() !== cell.letter.toLowerCase()) wrongKeySet.add(key);
        });
      }
    });
  }

  return (
    <div className="book-workbook__crossword">
      <div className="book-workbook__panel book-workbook__crossword-grid-wrap">
        <div
          className="book-workbook__crossword-grid"
          style={{ gridTemplateColumns: `repeat(${crossword.cols}, ${cellSize}px)`, gridAutoRows: `${cellSize}px` }}
        >
          {Array.from({ length: crossword.rows * crossword.cols }).map((_, position) => {
            const row = Math.floor(position / crossword.cols);
            const col = position % crossword.cols;
            const key = `${row}:${col}`;
            const cell = cellByKey[key];
            if (!cell) return <div key={key} />;
            const shown = checked && wrongKeySet.has(key) ? cell.letter.toUpperCase() : letters[key] || "";
            return (
              <button
                type="button"
                key={key}
                className={`book-workbook__cell${activeKeySet.has(key) ? " is-active" : ""}${
                  wrongKeySet.has(key) ? " is-wrong" : ""
                }`}
                style={{ fontSize: cellSize * 0.5 }}
                onClick={() => selectCell(key)}
              >
                {cell.number && <small>{cell.number}</small>}
                {shown}
              </button>
            );
          })}
        </div>
      </div>
      {activeWord && (
        <div className="book-workbook__clue-bar">
          <div className="book-workbook__clue-text">
            <span className="book-workbook__chip">
              {activeWord.number} {activeWord.direction === "across" ? "가로" : "세로"}
            </span>
            <span>{activeWord.clue}</span>
          </div>
          <div className="book-workbook__clue-entry">
            <button type="button" onClick={() => setActiveWordIndex((activeWordIndex - 1 + wordList.length) % wordList.length)}>
              ‹
            </button>
            <input
              value={draft}
              disabled={checked}
              maxLength={activeWord.cellList.length}
              placeholder={"_ ".repeat(activeWord.cellList.length).trim()}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => writeWord(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") setActiveWordIndex((activeWordIndex + 1) % wordList.length);
              }}
            />
            <button type="button" onClick={() => setActiveWordIndex((activeWordIndex + 1) % wordList.length)}>
              ›
            </button>
          </div>
        </div>
      )}
      <div className="book-workbook__panel book-workbook__clue-list">
        {["across", "down"].map((direction) => (
          <div key={direction}>
            <h4>{direction === "across" ? "가로 ACROSS" : "세로 DOWN"}</h4>
            {wordList.map((word, index) =>
              word.direction === direction ? (
                <button
                  type="button"
                  key={index}
                  className={`book-workbook__clue${index === activeWordIndex ? " is-active" : ""}`}
                  onClick={() => setActiveWordIndex(index)}
                >
                  <b>{word.number}</b>
                  <span>{word.clue}</span>
                  {checked && resultList && (
                    <em className={`book-workbook__mark ${resultList[index] ? "is-right" : "is-wrong"}`}>
                      {resultList[index] ? "O" : "X"}
                    </em>
                  )}
                </button>
              ) : null
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ───────── 워드서치 ─────────
const WordSearchActivity = ({ activity, response, onChange, checked }) => {
  const wordSearch = activity.wordSearch;
  const rowList = wordSearch.rows || [];
  const size = rowList.length;
  const value = response || {};
  const foundList = value.found || [];
  const foundCellList = value.foundCells || [];
  const [startCell, setStartCell] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);
  const gridRef = useRef(null);
  const letterSize = Math.max(22, Math.min(40, Math.floor((Math.min(window.innerWidth, 640) - 48) / size)));

  const lineCells = (from, to) => {
    if (!from || !to) return [];
    const rowStep = Math.sign(to[0] - from[0]);
    const colStep = Math.sign(to[1] - from[1]);
    const rowDistance = Math.abs(to[0] - from[0]);
    const colDistance = Math.abs(to[1] - from[1]);
    if (rowDistance !== 0 && colDistance !== 0 && rowDistance !== colDistance) return [];
    const length = Math.max(rowDistance, colDistance) + 1;
    return Array.from({ length }, (_, index) => [from[0] + rowStep * index, from[1] + colStep * index]);
  };

  const finish = (endCell) => {
    const cellList = lineCells(startCell, endCell);
    setStartCell(null);
    setHoverCell(null);
    if (cellList.length < 2) return;
    const text = cellList.map(([row, col]) => rowList[row][col]).join("");
    const reversed = text.split("").reverse().join("");
    const word = (wordSearch.words || []).find(
      (one) => !foundList.includes(one) && (one.toUpperCase() === text.toUpperCase() || one.toUpperCase() === reversed.toUpperCase())
    );
    if (word) onChange({ ...value, found: [...foundList, word], foundCells: [...foundCellList, cellList] });
  };

  const cellFromPoint = (event) => {
    const target = document.elementFromPoint(event.clientX, event.clientY);
    const cellText = target && target.getAttribute && target.getAttribute("data-cell");
    return cellText ? cellText.split(":").map(Number) : null;
  };

  const capsule = (cellList, color, key) => {
    const first = cellList[0];
    const last = cellList[cellList.length - 1];
    return (
      <line
        key={key}
        x1={first[1] * letterSize + letterSize / 2}
        y1={first[0] * letterSize + letterSize / 2}
        x2={last[1] * letterSize + letterSize / 2}
        y2={last[0] * letterSize + letterSize / 2}
        stroke={color}
        strokeOpacity="0.45"
        strokeWidth={letterSize * 0.78}
        strokeLinecap="round"
      />
    );
  };

  const answerCellList = checked ? (wordSearch.answer_cells || []) : [];
  const answerKeySet = new Set(answerCellList.map(([row, col]) => `${row}:${col}`));
  const draggingCells = startCell ? lineCells(startCell, hoverCell || startCell) : [];

  return (
    <div className="book-workbook__search">
      <div className="book-workbook__panel book-workbook__search-wrap">
        <div
          ref={gridRef}
          className="book-workbook__search-grid"
          style={{ gridTemplateColumns: `repeat(${size}, ${letterSize}px)`, gridAutoRows: `${letterSize}px` }}
          onPointerDown={(event) => {
            if (checked) return;
            const cell = cellFromPoint(event);
            if (!cell) return;
            if (startCell && (startCell[0] !== cell[0] || startCell[1] !== cell[1])) {
              finish(cell);
              return;
            }
            setStartCell(cell);
            setHoverCell(cell);
            if (gridRef.current && gridRef.current.setPointerCapture) gridRef.current.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!startCell) return;
            const cell = cellFromPoint(event);
            if (cell) setHoverCell(cell);
          }}
          onPointerUp={(event) => {
            if (!startCell) return;
            const cell = cellFromPoint(event);
            if (cell && (cell[0] !== startCell[0] || cell[1] !== startCell[1])) finish(cell);
          }}
        >
          <svg className="book-workbook__search-lines" width={size * letterSize} height={size * letterSize} aria-hidden="true">
            {foundCellList.map((cellList, index) => capsule(cellList, "#E8920A", `found-${index}`))}
            {draggingCells.length > 0 && capsule(draggingCells, "#0E141B", "dragging")}
          </svg>
          {rowList.map((row, rowIndex) =>
            row.split("").map((letter, colIndex) => (
              <span
                key={`${rowIndex}:${colIndex}`}
                data-cell={`${rowIndex}:${colIndex}`}
                className={`book-workbook__letter${checked && answerKeySet.has(`${rowIndex}:${colIndex}`) ? " is-answer" : ""}`}
                style={{ fontSize: letterSize * 0.5 }}
              >
                {letter}
              </span>
            ))
          )}
        </div>
      </div>
      <div className="book-workbook__panel book-workbook__word-list">
        {(wordSearch.words || []).map((word) => (
          <span key={word} className={`book-workbook__word${foundList.includes(word) ? " is-found" : ""}`}>
            {word}
          </span>
        ))}
      </div>
    </div>
  );
};

const ACTIVITY_COMPONENT = {
  choice: ChoiceActivity,
  input: InputActivity,
  written: WrittenActivity,
  match: MatchActivity,
  order: OrderActivity,
  multi: MultiActivity,
  classify: ClassifyActivity,
  gap: GapActivity,
  table: TableActivity,
  crossword: CrosswordActivity,
  word_search: WordSearchActivity,
};

// ───────── 본문 ─────────
const PassagePanel = ({ activity, chapterText, isMobile }) => {
  const [isOpen, setIsOpen] = useState(!isMobile);
  const paragraphList = activity.passage && activity.passage.length > 0 ? activity.passage : null;
  return (
    <div className={`book-workbook__passage${isMobile ? " book-workbook__passage--mobile" : ""}`}>
      <button type="button" className="book-workbook__passage-head" onClick={() => setIsOpen(!isOpen)}>
        <span>{paragraphList ? "지문" : "본문"}</span>
        <span className="book-workbook__passage-toggle">{isOpen ? "접기" : "펼치기"}</span>
      </button>
      {isOpen && (
        <div className="book-workbook__passage-body">
          {paragraphList
            ? paragraphList.map((paragraph, index) => (
                <p key={index}>
                  <b>{index + 1}</b> {renderBold(paragraph)}
                </p>
              ))
            : // 원문은 줄마다 끊겨 오고 문단 사이만 빈 줄이다. 빈 줄로 문단을 나누고 문단 안의 줄바꿈은 이어 붙인다.
              String(chapterText || "")
                .split(/\n\s*\n/)
                .map((paragraph) => paragraph.replace(/\s*\n\s*/g, " ").trim())
                .filter(Boolean)
                .map((paragraph, index) => <p key={index}>{paragraph}</p>)}
        </div>
      )}
    </div>
  );
};

const BookWorkbookPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const book = section?.question?.data;
  const chapterText = section?.question?.full_text || (book && book.chapter_text) || "";
  const seedText = `book:${section?.id || 0}`;
  const activityList = useMemo(() => buildBookActivityList(book, seedText), [book, seedText]);

  const [responses, setResponses] = useState({});
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [results, setResults] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(isNarrowScreen());
  const scrollRef = useRef(null);

  // section 이 아니라 section.id 에 건다. 저장할 때마다 assignmentSlice 가 section 을 통째로 갈아끼워서,
  // section 에 걸면 저장 직후 초기화가 다시 돌아 보던 활동에서 튄다(CLAUDE.md 직독직해 항목과 같은 함정).
  useEffect(() => {
    const saved = isPreview ? { responses: {}, checkedKeys: [], results: {} } : readSavedState(section?.answer);
    setResponses(saved.responses);
    setCheckedKeys(saved.checkedKeys);
    setResults(saved.results);
    const firstOpen = activityList.findIndex((activity) => !saved.checkedKeys.includes(activity.key));
    setCurrentIndex(isReview || firstOpen < 0 ? 0 : firstOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id, activityList.length]);

  useEffect(() => {
    const onResize = () => setIsMobile(isNarrowScreen());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [currentIndex]);

  if (activityList.length === 0) {
    return <div className="book-workbook__empty">이 세트에는 풀 문제가 없습니다.</div>;
  }

  const activity = activityList[Math.min(currentIndex, activityList.length - 1)];
  const isChecked = isReview || checkedKeys.includes(activity.key);
  const ActivityComponent = ACTIVITY_COMPONENT[activity.kind];
  const resultList = results[activity.key] || null;
  const isGraded = resultList && resultList.some((result) => result !== null);
  const rightCount = resultList ? resultList.filter((result) => result === true).length : 0;
  const gradedCount = resultList ? resultList.filter((result) => result !== null).length : 0;
  const hasWrittenOnly = activity.kind === "written" || activity.kind === "table";

  const buildAnswer = (nextResponses, nextCheckedKeys, nextResults) => [
    { type: STATE_TYPE, responses: nextResponses, checkedKeys: nextCheckedKeys, results: nextResults },
    ...activityList.flatMap((one) => {
      const reportList = nextResults[one.key] ? reportActivity(one, nextResponses[one.key]) : [];
      return (nextResults[one.key] || []).map((result, index) => ({
        activity: one.key,
        kind: one.kind,
        label: one.label,
        part: one.part,
        index,
        isCorrect: result,
        ...(reportList[index] || {}),
      }));
    }),
  ];

  const save = (nextResponses, nextCheckedKeys, nextResults, isFinished) => {
    const answer = buildAnswer(nextResponses, nextCheckedKeys, nextResults);
    if (isPreview || isReview) return;
    dispatch(setSection({ ...section, answer }));
    dispatch(
      updateSection({
        sectionId: section.id,
        data: isFinished ? { answer, is_finished: true } : { answer },
      })
    );
  };

  const handleChange = (next) => {
    if (isChecked) return;
    setResponses({ ...responses, [activity.key]: next });
  };

  const handleCheck = () => {
    const nextResults = { ...results, [activity.key]: gradeActivity(activity, responses[activity.key]) };
    const nextCheckedKeys = checkedKeys.includes(activity.key) ? checkedKeys : [...checkedKeys, activity.key];
    setResults(nextResults);
    setCheckedKeys(nextCheckedKeys);
    save(responses, nextCheckedKeys, nextResults, false);
  };

  const handleRetry = () => {
    const nextResponses = { ...responses };
    delete nextResponses[activity.key];
    const nextResults = { ...results };
    delete nextResults[activity.key];
    const nextCheckedKeys = checkedKeys.filter((key) => key !== activity.key);
    setResponses(nextResponses);
    setResults(nextResults);
    setCheckedKeys(nextCheckedKeys);
    save(nextResponses, nextCheckedKeys, nextResults, false);
  };

  const handleNext = () => {
    if (currentIndex < activityList.length - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }
    if (isReview) {
      setCurrentStep(2);
      return;
    }
    const remainingCount = activityList.filter((one) => !checkedKeys.includes(one.key)).length;
    if (remainingCount > 0) {
      alert(`아직 안 푼 활동이 ${remainingCount}개 있어요.`);
      const firstOpen = activityList.findIndex((one) => !checkedKeys.includes(one.key));
      if (firstOpen >= 0) setCurrentIndex(firstOpen);
      return;
    }
    save(responses, checkedKeys, results, true);
    alert("학습이 완료되었습니다.");
    setCurrentStep(2);
  };

  const showPassage = activity.kind !== "crossword" && activity.kind !== "word_search";

  return (
    <div className="book-workbook" style={{ height: getContentHeight(isMobile) }}>
      <div className="book-workbook__layout">
        {showPassage && !isMobile && <PassagePanel activity={activity} chapterText={chapterText} isMobile={false} />}
        <div className="book-workbook__main" ref={scrollRef}>
          <div className="book-workbook__head">
            <div className="book-workbook__head-row">
              <span className="book-workbook__chip">Q{currentIndex + 1}</span>
              <span className="book-workbook__part">{PART_LABEL[activity.part]}</span>
              <span className="book-workbook__counter">
                {currentIndex + 1} / {activityList.length}
              </span>
            </div>
            <div className="book-workbook__progress">
              <div style={{ width: `${((currentIndex + 1) / activityList.length) * 100}%` }} />
            </div>
            <div className="book-workbook__title">{activity.label}</div>
            {activity.instruction && <div className="book-workbook__instruction">{activity.instruction}</div>}
          </div>

          {showPassage && isMobile && <PassagePanel activity={activity} chapterText={chapterText} isMobile />}

          {ActivityComponent && (
            <ActivityComponent
              key={activity.key}
              activity={activity}
              response={responses[activity.key]}
              onChange={handleChange}
              checked={isChecked}
              resultList={resultList}
              seedText={`${seedText}:${activity.key}`}
            />
          )}

          <div className="book-workbook__foot">
            <span className="book-workbook__result">
              {isChecked && isGraded ? `${gradedCount}개 중 ${rightCount}개 맞았어요` : ""}
              {isChecked && !isGraded && hasWrittenOnly ? "모범 답과 비교해 보세요" : ""}
            </span>
            <div className="book-workbook__buttons">
              {currentIndex > 0 && (
                <button type="button" className="book-workbook__ghost" onClick={() => setCurrentIndex(currentIndex - 1)}>
                  이전
                </button>
              )}
              {isChecked && !isReview && (
                <button type="button" className="book-workbook__ghost" onClick={handleRetry}>
                  다시 하기
                </button>
              )}
              {!isChecked ? (
                <button type="button" className="book-workbook__primary" onClick={handleCheck}>
                  {hasWrittenOnly ? "제출하기" : "채점하기"}
                </button>
              ) : (
                <button type="button" className="book-workbook__primary" onClick={handleNext}>
                  {currentIndex < activityList.length - 1 ? "다음" : "끝내기"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookWorkbookPage;
